import React, { useState, useEffect } from 'react';
import {
    X,
    GripVertical,
    ChevronDownIcon,
    ChevronRightIcon,
    Trash2,
    Edit2,
    ArrowUpIcon,
    ArrowDownIcon
} from 'lucide-react';
import { Gift } from '../../services/giftApi';
import { giftApi } from '../../services/giftApi';
import { settingsApi } from '../../services/settingsApi';

interface GiftCategoryManagerProps {
    gifts: Gift[];
    onClose: () => void;
    onGiftsUpdated: () => void;
    showSuccess: (title: string, message: string) => void;
    showError: (title: string, message: string) => void;
}

export const GiftCategoryManager: React.FC<GiftCategoryManagerProps> = ({ gifts, onClose, onGiftsUpdated, showSuccess, showError }) => {
    const [categories, setCategories] = useState<string[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCategory, setEditingCategory] = useState<{old: string, new: string} | null>(null);

    // Drag and drop state for Categories
    const [draggedCatIndex, setDraggedCatIndex] = useState<number | null>(null);
    const [dragOverCatIndex, setDragOverCatIndex] = useState<number | null>(null);

    // Drag and drop state for Gifts
    const [draggedGiftRef, setDraggedGiftRef] = useState<{ catId: string; index: number } | null>(null);
    const [dragOverGiftRef, setDragOverGiftRef] = useState<{ catId: string; index: number } | null>(null);

    // Map of gifts by Category
    const [categoryGifts, setCategoryGifts] = useState<Record<string, Gift[]>>({});

    useEffect(() => {
        loadData();
    }, [gifts]);

    const loadData = async () => {
        let custom: string[] = [];
        try {
            const settings = await settingsApi.getSettings();
            custom = settings.gift_category_order || [];
        } catch (e) {
            console.error('Failed to load global category settings, falling back to local', e);
            const stored = localStorage.getItem('custom_gift_categories');
            try {
                const parsed = stored ? JSON.parse(stored) : [];
                custom = Array.isArray(parsed) ? parsed : [];
            } catch(e) {}
        }
        
        // Extract uniquely from gifts - Guarded
        const extracted = Array.isArray(gifts)
            ? Array.from(new Set(gifts.map(g => g.category || 'Uncategorized'))).filter(Boolean) as string[]
            : [];
        
        // Merge without losing custom order, but adding any derived ones that are missing
        const combined = Array.from(new Set([...custom, ...extracted]));
        setCategories(combined);
        
        // Update local storage and backend if needed (sync)
        localStorage.setItem('custom_gift_categories', JSON.stringify(combined));

        // Bucket gifts - Guarded
        const map: Record<string, Gift[]> = {};
        if (Array.isArray(gifts)) {
            combined.forEach(cat => {
                let catGifts = (gifts || []).filter(g => (g.category || 'Uncategorized') === cat);
                catGifts.sort((a,b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                map[cat] = catGifts;
            });
        }
        setCategoryGifts(map);
    };

    const saveCategories = async (updated: string[]) => {
        try {
            setCategories(updated);
            localStorage.setItem('custom_gift_categories', JSON.stringify(updated));
            await settingsApi.updateSettings({ gift_category_order: updated });
        } catch (e) {
            showError('Sync Error', 'Failed to save category order to server.');
        }
    };

    const toggleCategory = (cat: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(cat)) next.delete(cat);
            else next.add(cat);
            return next;
        });
    };

    // Category Reordering
    const reorderCategories = (from: number, to: number) => {
        const arr = [...categories];
        const [moved] = arr.splice(from, 1);
        arr.splice(to, 0, moved);
        saveCategories(arr);
        showSuccess('Category Management', 'Categories reordered');
    };

    // Category CRUD
    const handleAddCategory = () => {
        if (!newCategoryName.trim()) return;
        const name = newCategoryName.trim();
        if (categories.includes(name)) {
            showError('Invalid Category', 'Category already exists');
            return;
        }
        const updated = [...categories, name];
        saveCategories(updated);
        setNewCategoryName('');
        showSuccess('Category Management', 'Category created');
    };

    const handleDeleteCategory = async (cat: string) => {
        if (!window.confirm(`Are you sure you want to delete the category "${cat}"? Gifts inside will become Uncategorized.`)) return;
        
        const giftsInCat = categoryGifts[cat] || [];
        try {
            await Promise.all(giftsInCat.map(g => giftApi.updateGift(g.id, { category: 'Uncategorized' })));
            const updated = categories.filter(c => c !== cat);
            saveCategories(updated);
            onGiftsUpdated();
            showSuccess('Category Deleted', `Category removed and ${giftsInCat.length} gifts uncategorized.`);
        } catch (e) {
            showError('Deletion Failed', 'Failed to remove category safely.');
        }
    };

    const handleRenameCategory = async () => {
        if (!editingCategory) return;
        const oldName = editingCategory.old;
        const newName = editingCategory.new.trim();
        
        if (!newName || oldName === newName) {
            setEditingCategory(null);
            return;
        }
        
        const giftsInCat = categoryGifts[oldName] || [];
        try {
            await Promise.all(giftsInCat.map(g => giftApi.updateGift(g.id, { category: newName })));
            const updated = categories.map(c => c === oldName ? newName : c);
            saveCategories(updated);
            setEditingCategory(null);
            onGiftsUpdated();
            showSuccess('Category Renamed', 'Category renamed successfully.');
        } catch (e) {
            showError('Rename Failed', 'Failed to rename category.');
        }
    };

    // Gift Reordering within Category
    const reorderGifts = async (catId: string, fromIndex: number, toIndex: number) => {
        const catList = [...(categoryGifts[catId] || [])];
        if (fromIndex === toIndex || fromIndex < 0 || toIndex >= catList.length) return;

        const [moved] = catList.splice(fromIndex, 1);
        catList.splice(toIndex, 0, moved);

        // Optimistically update UI
        setCategoryGifts(prev => ({...prev, [catId]: catList}));

        try {
            await Promise.all((catList || []).map((g, idx) => giftApi.updateGift(g.id, { sortOrder: idx })));
            onGiftsUpdated();
        } catch (e) {
            showError('Ordering Failed', 'Error saving sort order');
        }
    };

    // UI Renderers
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden transform transition-all">
                <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 flex-shrink-0">
                    <div>
                        <h3 className="text-xl font-bold text-gray-800">Manage Categories & Ordering</h3>
                        <p className="text-sm text-gray-500">Drag to reorder categories and products.</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                    <div className="space-y-4">
                        {categories.map((cat, catIndex) => (
                            <div 
                                key={cat} 
                                className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 
                                    ${draggedCatIndex === catIndex ? 'opacity-50 scale-105' : ''}
                                    ${dragOverCatIndex === catIndex ? 'border-blue-500 shadow-md ring-1 ring-blue-500' : ''}`}
                                draggable
                                onDragStart={(e) => {
                                    setDraggedCatIndex(catIndex);
                                    e.dataTransfer.effectAllowed = 'move';
                                }}
                                onDragEnd={() => {
                                    setDraggedCatIndex(null);
                                    setDragOverCatIndex(null);
                                }}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragOverCatIndex(catIndex);
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (draggedCatIndex !== null && draggedCatIndex !== catIndex) {
                                        reorderCategories(draggedCatIndex, catIndex);
                                    }
                                }}
                            >
                                <div className="p-4 flex items-center gap-4 bg-white hover:bg-gray-50 transition-colors group">
                                    <div className="cursor-move p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded">
                                        <GripVertical className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 flex items-center justify-between">
                                        {editingCategory?.old === cat ? (
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    autoFocus
                                                    value={editingCategory.new}
                                                    onChange={e => setEditingCategory({ ...editingCategory, new: e.target.value })}
                                                    onKeyDown={e => e.key === 'Enter' && handleRenameCategory()}
                                                    className="px-3 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                                />
                                                <button onClick={handleRenameCategory} className="px-3 py-1 bg-blue-600 text-white text-sm rounded">Save</button>
                                                <button onClick={() => setEditingCategory(null)} className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded">Cancel</button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3">
                                                <h4 className="font-semibold text-gray-800 text-lg">{cat}</h4>
                                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">
                                                    Pos {catIndex + 1}
                                                </span>
                                                <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full font-medium">
                                                    {(categoryGifts[cat] || []).length} items
                                                </span>
                                            </div>
                                        )}
                                        
                                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={() => setEditingCategory({ old: cat, new: cat })}
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteCategory(cat)}
                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <button 
                                        onClick={() => toggleCategory(cat)}
                                        className="p-2 ml-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                    >
                                        {expandedCategories.has(cat) ? <ChevronDownIcon className="w-5 h-5"/> : <ChevronRightIcon className="w-5 h-5"/>}
                                    </button>
                                </div>

                                {/* Expanded Gifts */}
                                {expandedCategories.has(cat) && (
                                    <div className="border-t border-gray-100 bg-gray-50/50 p-4 space-y-2">
                                        {(categoryGifts[cat] || []).length === 0 ? (
                                            <p className="text-sm text-gray-400 text-center py-4">No specific items isolated in this category.</p>
                                        ) : (
                                            (categoryGifts[cat] || []).map((gift, giftIdx) => (
                                                <div 
                                                    key={gift.id}
                                                    draggable
                                                    onDragStart={(e) => {
                                                        e.stopPropagation();
                                                        setDraggedGiftRef({ catId: cat, index: giftIdx });
                                                        e.dataTransfer.effectAllowed = 'move';
                                                    }}
                                                    onDragEnd={(e) => {
                                                        e.stopPropagation();
                                                        setDraggedGiftRef(null);
                                                        setDragOverGiftRef(null);
                                                    }}
                                                    onDragOver={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setDragOverGiftRef({ catId: cat, index: giftIdx });
                                                    }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (draggedGiftRef?.catId === cat && draggedGiftRef.index !== giftIdx) {
                                                            reorderGifts(cat, draggedGiftRef.index, giftIdx);
                                                        }
                                                    }}
                                                    className={`flex items-center gap-4 bg-white p-3 rounded-lg border shadow-sm cursor-move transition-all duration-200
                                                        ${draggedGiftRef?.catId === cat && draggedGiftRef?.index === giftIdx ? 'opacity-50 scale-[1.02]' : ''}
                                                        ${dragOverGiftRef?.catId === cat && dragOverGiftRef?.index === giftIdx ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-gray-200'}
                                                    `}
                                                >
                                                    <GripVertical className="w-4 h-4 text-gray-400" />
                                                    <img 
                                                        src={gift.imageUrl} 
                                                        alt={gift.name} 
                                                        className="w-12 h-12 object-contain rounded bg-gray-50 border border-gray-100" 
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <h5 className="font-medium text-gray-800 text-sm truncate">{gift.name}</h5>
                                                        <p className="text-xs text-gray-500 mt-0.5">Price: {gift.currency} {gift.price} | Stock: {gift.stock}</p>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <button 
                                                            onClick={() => reorderGifts(cat, giftIdx, giftIdx - 1)}
                                                            disabled={giftIdx === 0}
                                                            className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30"
                                                        >
                                                            <ArrowUpIcon className="w-4 h-4" />
                                                        </button>
                                                        <button 
                                                            onClick={() => reorderGifts(cat, giftIdx, giftIdx + 1)}
                                                            disabled={giftIdx === (categoryGifts[cat]?.length || 0) - 1}
                                                            className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30"
                                                        >
                                                            <ArrowDownIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-6 border-t border-gray-200 bg-white flex-shrink-0">
                    <div className="flex gap-4">
                        <input
                            placeholder="Add New Category..."
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <button 
                            onClick={handleAddCategory}
                            disabled={!newCategoryName.trim()}
                            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                            Add
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
